import { ThemeProvider } from "@repo/ui/components/theme-provider";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { type ComponentType, StrictMode, useSyncExternalStore } from "react";
import { createRoot } from "react-dom/client";
import { routeTree } from "./routeTree.gen";
import { PortalAccessDeniedError, queryClient, restoreProfile } from "./shared/edge/client";
import { session } from "./shared/edge/session";
import "./styles.css";
// Create router
const router = createRouter({
  routeTree,
  context: {
    profile: undefined,
    queryClient,
  },
  defaultPreload: "intent",
});

// Type registration
declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    /** The page fills the viewport and scrolls inside itself, e.g. the document viewer. */
    fullBleed?: boolean;
    /** The page carries its own title; the app header is dropped (phones keep the menu button). */
    hideHeader?: boolean;
    /** The page's own breadcrumb segment, shown after its section in the header. */
    crumb?: ComponentType;
  }
  interface Register {
    router: typeof router;
  }
}

function App() {
  const profile = useSyncExternalStore(session.subscribe, session.getProfile);
  return <RouterProvider router={router} context={{ profile, queryClient }} />;
}
const element = document.getElementById("root");
const root = element ? createRoot(element) : undefined;
async function initApp() {
  let unavailable = false;
  try {
    await restoreProfile();
  } catch (error) {
    if (error instanceof PortalAccessDeniedError) {
      await router.navigate({ to: "/login", search: { error: "access-denied" } });
    }
    unavailable = session.hasTokens();
  }
  root?.render(
    <StrictMode>
      <ThemeProvider defaultTheme="light" storageKey="patient-theme">
        <QueryClientProvider client={queryClient}>
          {unavailable ? (
            <div role="alert" className="p-6">
              Could not connect. Your sign-in has been kept.
              <button type="button" onClick={() => void initApp()}>
                Retry
              </button>
            </div>
          ) : (
            <App />
          )}
        </QueryClientProvider>
      </ThemeProvider>
    </StrictMode>,
  );
}
void initApp();
